import {Component, EventEmitter, Input, OnChanges, Output, SimpleChanges} from '@angular/core';
import {Dialog} from 'primeng/dialog';
import {TableModule} from 'primeng/table';
import {Select} from 'primeng/select';
import {Button} from 'primeng/button';
import {Message} from 'primeng/message';
import {FormsModule} from '@angular/forms';
import {NgFor, NgIf} from '@angular/common';
import {CustomField} from '../../../custom-field/custom-field';
import {Container} from '../../../container/container';

export interface ContainerImportRow {
    containerFields: Partial<{
        id: string;
        unLoCodeLoadingPort: string;
        unLoCodeDestinationPort: string;
        masterBillOfLadingReference: string;
        oceanCarrierCode: string;
        lastPushToShippeo: string;
    }>;
    customFieldValues: { customFieldId: string; value: string }[];
}

interface SelectOption {
    label: string;
    value: string | null;
}

interface SelectOptionGroup {
    label: string;
    items: SelectOption[];
}

@Component({
    selector: 'app-container-import-dialog',
    standalone: true,
    imports: [Dialog, TableModule, Select, Button, Message, FormsModule, NgFor, NgIf],
    templateUrl: './container-import-dialog.html',
})
export class ContainerImportDialog implements OnChanges {
    @Input() visible = false;
    @Output() visibleChange = new EventEmitter<boolean>();

    @Input() customFields: CustomField[] = [];
    @Input() existingContainers: Container[] = [];

    @Output() confirmImport = new EventEmitter<ContainerImportRow[]>();

    parsedRows: string[][] = [];
    columnCount = 0;
    columnMappings: (string | null)[] = [];
    groupedOptions: SelectOptionGroup[] = [];
    errorMessage: string | null = null;

    private readonly CONTAINER_FIELDS: SelectOption[] = [
        {label: 'ID (for matching existing)', value: 'id'},
        {label: 'UN/LOCODE Loading Port', value: 'unLoCodeLoadingPort'},
        {label: 'UN/LOCODE Destination Port', value: 'unLoCodeDestinationPort'},
        {label: 'Master Bill of Lading Reference', value: 'masterBillOfLadingReference'},
        {label: 'Ocean Carrier Code', value: 'oceanCarrierCode'},
        {label: 'Module Wattage', value: 'moduleWattage'},
        {label: 'Purchase Order ID', value: 'purchaseOrderId'},
        {label: 'ETD Factory', value: 'etdFactory'},
        {label: 'ETD Port', value: 'etdPort'},
        {label: 'ETD Destination Port', value: 'etaDestinationPort'},
        {label: 'Inbond Date', value: 'inbondDate'},
    ];

    private readonly CONTAINER_FIELD_TYPES = {
        id: 'string',
        unLoCodeLoadingPort: 'string',
        unLoCodeDestinationPort: 'string',
        masterBillOfLadingReference: 'string',
        oceanCarrierCode: 'string',
        moduleWattage: 'number',
        purchaseOrderId: 'string',
        etdFactory: 'date',
        etdPort: 'date',
        etaDestinationPort: 'date',
        inbondDate: 'date',
    };

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['customFields']) {
            this.buildGroupedOptions();
        }
    }

    private buildGroupedOptions(): void {
        this.groupedOptions = [
            {
                label: 'Container Fields',
                items: this.CONTAINER_FIELDS,
            },
            {
                label: 'Custom Fields',
                items: this.customFields.map(cf => ({
                    label: cf.name,
                    value: `custom:${cf.id}`,
                })),
            },
        ];
    }

    get columnIndices(): number[] {
        return Array.from({length: this.columnCount}, (_, i) => i);
    }

    async pasteFromClipboard(): Promise<void> {
        try {
            const text = await navigator.clipboard.readText();
            if (!text.trim()) {
                this.errorMessage = 'Clipboard is empty.';
                return;
            }
            this.parseClipboardText(text);
            this.errorMessage = null;
        } catch {
            this.errorMessage = 'Could not read clipboard. Ensure clipboard access is permitted in your browser.';
        }
    }

    private parseClipboardText(text: string): void {
        const rows = text
            .trim()
            .split(/\r?\n/)
            .map(row => row.split('\t'));

        this.parsedRows = rows;
        this.columnCount = Math.max(...rows.map(r => r.length));
        this.columnMappings = Array(this.columnCount).fill(null);
    }

    confirm(): void {
        const importRows: ContainerImportRow[] = this.parsedRows.map(row => {
            const containerFields: ContainerImportRow['containerFields'] = {};
            const customFieldValues: ContainerImportRow['customFieldValues'] = [];

            this.columnMappings.forEach((mapping, colIndex) => {
                if (!mapping) return;
                let value = (row[colIndex] ?? '').trim();

                if (mapping.startsWith('custom:')) {
                    customFieldValues.push({customFieldId: mapping.replace('custom:', ''), value});
                } else {
                    // @ts-ignore
                    if (this.CONTAINER_FIELD_TYPES[mapping] === 'date') {
                        (containerFields as Record<string, string|Date>)[mapping] = new Date(value);
                    } else {
                        (containerFields as Record<string, string>)[mapping] = value;
                    }
                }
            });

            return {containerFields, customFieldValues};
        });

        this.confirmImport.emit(importRows);
        this.close();
    }

    close(): void {
        this.visibleChange.emit(false);
        this.parsedRows = [];
        this.columnMappings = [];
        this.columnCount = 0;
        this.errorMessage = null;
    }
}
